import React from 'react'

import clsx from 'clsx'
import dayjs from 'dayjs'

import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import Link from 'components/Link'
import TagWithHover from 'components/TagWithHover'
import Typography from 'components/Typography'
import UserName from 'components/UserName'
import { EventContext, useEventContext } from 'context/EventContext'
import { formatAvatarInitials } from 'helpers/formatters'

import classes from './Snippet.module.scss'

const Tag = ({ snippet }) => {
  const getTagVariant = () => {
    if (dayjs().diff(dayjs(snippet?.created), 'month') <= 12) {
      return 'Highly relevant'
    }
    if (
      (snippet?.frequency_of_use === 'daily' && snippet?.time_used !== 'Less than 6 months') ||
      ((snippet?.time_used === '1-2 years' || snippet?.time_used === '2+ years') &&
        snippet?.frequency_of_use === 'weekly')
    ) {
      return 'Frequent user'
    }
    return null
  }
  const tagVariant = getTagVariant()

  return <div className={classes.tagsContainer}>{tagVariant && <TagWithHover variant={tagVariant} size="tiny" />}</div>
}

/** @type {import('./types').default} */
const Snippet = ({
  className,
  type = '',
  snippet,
  hideReviewerName = false, //not used
  size = 'small',
  writeReview = false,
  engagementComponent,
  ...props
}) => {
  const { text, href, user_name, avatar } = snippet
  const isLarge = size === 'large'
  const isPros = type === 'pros'
  const labelSingular = type.split('s')[0] // TODO Remove this operation for regular users
  const eventContext = useEventContext()

  const readFullReviewEvent = {
    engagementComponent: engagementComponent ? engagementComponent : `product_reviews_${type}`,
    engagementDestination: 'go-to_product_reviews_single-review',
    engagementDetails: 'text-link_read-the-full-review',
    name: 'engagement_product_click',
  }

  const AvatarContent = () =>
    snippet.is_anonymous ? (
      <div className={classes.userIconBgExp}>
        <Icon icon={'circle-user'} size={24} />
      </div>
    ) : (
      <Avatar src={avatar} className={classes.userAvatarExp}>
        {formatAvatarInitials(user_name)}
      </Avatar>
    )

  const UserDetail = ({ job }) => {
    return (
      <Typography variant="tight-xxs" className={classes.anonymity}>
        {!snippet.is_anonymous ? job : 'Anonymity request'}
      </Typography>
    )
  }

  const quotedText = text.match(/“(.*?)”/) ? text : `“${text}”`

  return (
    <div
      className={clsx(
        'Snippet',
        classes.snippet,
        classes[isLarge ? 'snippetLarge' : 'snippetSmall'],
        classes.snippetContainerExp,
        className
      )}
      data-testid={`snippet-${labelSingular}`}
      {...props}
    >
      {type && (
        <div className={classes.iconContainerExp}>
          <Icon
            size={16}
            className={clsx(classes[isPros ? 'iconPros' : 'iconCons'], classes.icon)}
            icon={type === 'pros' ? 'caret-circle-up' : 'caret-circle-down'}
          />

          <EventContext
            value={{
              engagementComponent: engagementComponent ? engagementComponent : `product_reviews_${type}`,
            }}
          >
            <Tag snippet={snippet} />
          </EventContext>
        </div>
      )}
      <div
        className={clsx(classes.snippetContainer, {
          [classes.snippetContainerPadding]: isLarge,
        })}
      >
        <Typography component="div" variant="body-xs" className={classes.snippetText}>
          {quotedText}
        </Typography>
        {href && writeReview && (
          <div className={classes.readFullReview}>
            <Link
              {...eventContext.addEvent(readFullReviewEvent)}
              className={classes.readReviewLink}
              variant="body-xs"
              disableSeo
              href={href}
            >
              Read the full review
            </Link>
          </div>
        )}
        <div className={clsx(classes.footerExp, classes.footer)}>
          {!hideReviewerName && (
            <div className={classes.reviewerName}>
              <AvatarContent />
              <EventContext
                value={{
                  engagementComponent: engagementComponent ? engagementComponent : `product_reviews_${type}`,
                }}
              >
                <div>
                  <UserName
                    isSnippet
                    userName={user_name}
                    isAnonymous={snippet.is_anonymous}
                    // same as is_verified for reviews
                    isVerified={snippet.is_authenticated}
                    userInfo={snippet}
                  />
                  <UserDetail job={snippet?.job_title} />
                </div>
              </EventContext>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Snippet
